import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import cx from 'classnames';

import { Box } from '../LayoutPrimitives';
import RadioToggle from '../RadioToggle';
import Text from '../Text';
import { VisualOnlyLabel } from '../Label';
import warnInDev from '../../../helpers/warnInDev';
import announce from '../../../helpers/announce';

import Hours from './Hours';
import Minutes from './Minutes';

const AM = 'AM';
const PM = 'PM';

const radioToggleOptions = [
    {
        label: AM,
        value: AM,
    },
    {
        label: PM,
        value: PM,
    },
];

export default class TimePicker extends React.PureComponent {
    static propTypes = {
        /** id used for accessibility */
        id: PropTypes.string,

        /** Label contents, usually text */
        label: PropTypes.node,

        /** The timezone, usually text. Is displayed next to the label */
        timezone: PropTypes.node,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Default value used if value is nil. In the format H:mm, where hours are 0-23
         * and minutes are 00-59 */
        defaultValue: PropTypes.string,

        /** Used if component is controlled. In the format H:mm, where hours are 0-23
         * and minutes are 00-59 */
        value: PropTypes.string,

        /** Change handler */
        onChange: PropTypes.func,

        /** Function fired when the field is blurred */
        onBlur: PropTypes.func,

        /** Id to be passed to the input's aria-describedby property */
        ariaDescribedby: PropTypes.string,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Contents of the hint message, usually text */
        hintText: PropTypes.node,

        /** Performs the validation as soon as the component mounts */
        shouldValidateOnMount: PropTypes.bool,

        /** Enables validation as you type */
        shouldValidateOnUpdate: PropTypes.bool,

        /** Callback invoked when isInvalid/errorText changes state. Returns an
        object with the id, isValid, and errorText */
        handleNewErrorState: PropTypes.func,
    };

    static defaultProps = {
        id: 'time-picker',
        label: 'Time',
        isInvalid: false,
        isDisabled: false,
        shouldValidateOnMount: false,
        shouldValidateOnUpdate: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            hours: null,
            minutes: null,
            timeOfDay: AM,
            isPristine: !(props.value || props.defaultValue),
            isInvalid: props.isInvalid || !!props.errorText || false,
            errorText: props.errorText || undefined,
        };

        this.errorState = {
            isInvalid: props.isInvalid,
            errorText: props.errorText,
        };
    }

    componentDidMount() {
        const { value, shouldValidateOnMount } = this.props;
        const stateObject = this.convertValueToStateObject();

        if (stateObject) {
            this.setState({
                ...stateObject,
            });
        }

        if (value && shouldValidateOnMount) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.validate();
            }, 0);
        }
    }

    componentDidUpdate(prevProps) {
        const { value, shouldValidateOnUpdate, isInvalid, errorText } = this.props;
        const stateObject = this.convertValueToStateObject();

        if (value !== prevProps.value && !isEqual(this.state, stateObject)) {
            this.setState({
                ...stateObject,
            });
        }

        if (value && shouldValidateOnUpdate) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => {
                this.validate();
            }, 0);
        } else if (
            isInvalid !== prevProps.isInvalid ||
            errorText !== prevProps.errorText
        ) {
            const hasErrorText = typeof errorText === 'string' && errorText.length > 0;

            this.setState({
                isInvalid: isInvalid || hasErrorText,
                errorText,
            });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    clearError = () => {
        this.setState({
            isInvalid: false,
            errorText: undefined,
        });
    };

    convertValueToStateObject = () => {
        const { value, defaultValue } = this.props;
        const valueToUse = value || defaultValue;

        if (valueToUse) {
            const [hoursString, minutesString] = valueToUse.split(':');
            let hours = parseInt(hoursString, 10);
            let minutes = parseInt(minutesString, 10);

            // initial corrections to prevent bad state
            if (hours > 23) {
                hours = 0;
                warnInDev(
                    'TimePicker: You supplied an invalid value! Hours were greater than 23 so were reset to 0. Values should be in 24 hour time format where 0:00 is midnight and 23:59 is 11:59PM'
                );
            }

            if (minutes > 59) {
                minutes = 0;
                warnInDev(
                    'TimePicker: You supplied an invalid value! Minutes were greater than 59 so were reset to 0. Values should be in 24 hour time format where 0:00 is midnight and 23:59 is 11:59PM'
                );
            }

            const timeOfDay = hours > 11 ? PM : AM;

            if (hours === 0) {
                hours = 12;
            } else if (hours > 12) {
                hours -= 12;
            }

            const stateObject = {
                hours,
                minutes,
                timeOfDay,
            };

            return stateObject;
        }

        return null;
    };

    convertToSingleString = () => {
        const { hours, minutes, timeOfDay } = this.state;

        if (!hours || isNil(minutes) || !timeOfDay) {
            return null;
        }

        let adjustedHours;

        if (hours === 12 && timeOfDay === AM) {
            adjustedHours = '00';
        } else if (hours === 12 && timeOfDay === PM) {
            adjustedHours = '12';
        } else if (timeOfDay === PM) {
            adjustedHours = `${hours + 12}`;
        } else {
            adjustedHours = `${hours}`;
        }

        const adjustedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;

        const singleString = `${adjustedHours}:${adjustedMinutes}`;
        return singleString;
    };

    handleHoursChange = (hours) => {
        this.setState(
            {
                hours,
                isPristine: false,
            },
            () => {
                this.attemptToSendOnChange();
            }
        );
    };

    handleMinutesChange = (minutes) => {
        this.setState(
            {
                minutes,
                isPristine: false,
            },
            () => {
                this.attemptToSendOnChange();
            }
        );
    };

    handleTimeOfDayChange = (e) => {
        this.setState(
            {
                timeOfDay: e.target.value,
                isPristine: false,
            },
            () => {
                this.attemptToSendOnChange();
            }
        );
    };

    handleNewErrorState = ({ isInvalid, errorText }) => {
        const { handleNewErrorState, id } = this.props;

        // bail if no handleNewErrorState exists
        if (!handleNewErrorState) {
            return;
        }

        // compare new state to old state. If different, invoke callback
        if (
            isInvalid !== this.errorState.isInvalid ||
            errorText !== this.errorState.errorText
        ) {
            handleNewErrorState({
                id,
                isInvalid,
                errorText,
            });

            this.errorState = {
                isInvalid,
                errorText,
            };
        }
    };

    handleBlur = () => {
        const { onBlur } = this.props;
        const { isPristine, isInvalid, errorText } = this.state;
        const value = this.convertToSingleString();

        if (!isPristine) {
            const validationState = this.validate();

            if (onBlur) {
                onBlur({ value, ...validationState });
            }

            if (validationState.isInvalid && validationState.errorText) {
                announce(validationState.errorText, 'assertive');
            }
        } else if (onBlur) {
            onBlur({ value, isInvalid, errorText });
        }
    };

    incrementHours = () => {
        const { hours } = this.state;
        let newHours = 1;

        if (!isNil(hours)) {
            newHours = hours === 12 ? 1 : hours + 1;
        }

        this.handleHoursChange(newHours);
    };

    decrementHours = () => {
        const { hours } = this.state;
        let newHours = 12;

        if (!isNil(hours)) {
            newHours = hours === 1 ? 12 : hours - 1;
        }

        this.handleHoursChange(newHours);
    };

    incrementMinutes = () => {
        const { minutes } = this.state;
        let newMinutes = 0;

        if (!isNil(minutes)) {
            newMinutes = minutes >= 45 ? 0 : minutes + 15;
        }

        this.handleMinutesChange(newMinutes);
    };

    decrementMinutes = () => {
        const { minutes } = this.state;
        let newMinutes = 0;

        if (!isNil(minutes)) {
            newMinutes = minutes === 0 ? 45 : minutes - 15;
        }

        this.handleMinutesChange(newMinutes);
    };

    attemptToSendOnChange = () => {
        const { hours, minutes, timeOfDay } = this.state;
        const { onChange } = this.props;

        if (onChange && hours && !isNil(minutes) && timeOfDay) {
            const newValue = this.convertToSingleString();
            onChange(newValue);
        }
    };

    validate = () => {
        const { isRequired, id, label, value } = this.props;
        const valueToValidate = value || this.convertToSingleString();

        const validationState = {
            isInvalid: false,
            errorText: null,
            id,
        };

        // When the field is empty
        if (!valueToValidate) {
            if (isRequired) {
                const errorText =
                    typeof label === 'string'
                        ? `${label} is required.`
                        : 'This field is required';
                validationState.isInvalid = true;
                validationState.errorText = errorText;
            } else {
                // Reset error state
                this.clearError();
            }
        }

        // When there is a value
        if (valueToValidate) {
            const stringLength = valueToValidate.length;

            // String is too long or missing the colon
            if (stringLength > 5 || !valueToValidate.includes(':')) {
                validationState.isInvalid = true;
                validationState.errorText = 'Invalid time format.';
            } else {
                const [hoursString, minutesString] = valueToValidate.split(':');
                const hours = parseInt(hoursString, 10);
                const minutes = parseInt(minutesString, 10);

                if (
                    isNaN(hours) ||
                    isNaN(minutes) ||
                    hours < 0 ||
                    hours > 23 ||
                    minutes < 0 ||
                    minutes > 59
                ) {
                    validationState.isInvalid = true;
                    validationState.errorText = 'Invalid time format.';
                }
            }
        }

        // Update local component state
        this.setState(validationState);
        this.handleNewErrorState(validationState);

        // Return values to be passed on to onBlur
        return validationState;
    };

    render() {
        const {
            id,
            label,
            timezone,
            isRequired,
            hintText,
            ariaDescribedby,
            isDisabled,
        } = this.props;
        const { timeOfDay, hours, minutes, errorText, isInvalid } = this.state;

        return (
            <div data-component="TimePicker" id={id}>
                <div aria-hidden="true" data-tp-label id={`${id}-label`}>
                    <Box alignX="space-between" alignY="baseline">
                        <VisualOnlyLabel isRequired={isRequired}>{label}</VisualOnlyLabel>
                        {timezone && <Text size="xs">{timezone}</Text>}
                    </Box>
                </div>

                <div
                    data-time-picker-wrap
                    role="group"
                    aria-label={timezone ? `${label}, ${timezone}` : label}
                    aria-describedby={cx({
                        [`${id}-error`]: !!errorText,
                        [ariaDescribedby]: !!ariaDescribedby,
                    })}
                >
                    <Hours
                        id={id}
                        hours={hours}
                        onChange={this.handleHoursChange}
                        isRequired={isRequired}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        onBlur={this.handleBlur}
                        increment={this.incrementHours}
                        decrement={this.decrementHours}
                    />
                    <span className="sarsa-time-picker-colon">:</span>
                    <Minutes
                        id={id}
                        minutes={minutes}
                        onChange={this.handleMinutesChange}
                        isRequired={isRequired}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        onBlur={this.handleBlur}
                        increment={this.incrementMinutes}
                        decrement={this.decrementMinutes}
                    />
                    <RadioToggle
                        options={radioToggleOptions}
                        value={timeOfDay}
                        onChange={this.handleTimeOfDayChange}
                        isRequired={isRequired}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        onBlur={this.handleBlur}
                    />
                </div>

                <div
                    id={`${id}-error`}
                    className="sarsa-field-error-message"
                    aria-live="assertive"
                >
                    {errorText && (
                        <>
                            <span className="req" aria-hidden="true">
                                *
                            </span>
                            {errorText}
                        </>
                    )}
                </div>

                <div
                    id={`${id}-hint`}
                    className="sarsa-field-hint-message"
                    aria-live="polite"
                >
                    {!errorText && hintText && (
                        <>
                            <span className="hint" aria-hidden="true">
                                *
                            </span>
                            {hintText}
                        </>
                    )}
                </div>
            </div>
        );
    }
}
